import React, { useEffect, useMemo, useContext, useState, Suspense } from 'react'
import InfoIcon from '@mui/icons-material/Info'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { motion, AnimatePresence } from 'framer-motion'
import { isEmpty } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import {
  Route,
  Switch,
  Redirect,
  useHistory,
  useLocation,
  useRouteMatch,
  matchPath,
} from 'react-router-dom'
import { selectFeatureIncludes, FeatureDict } from '@authed/selectors'
import { DatasetTracker } from '@components'
import { Loader } from '@components/loader'
import { DatasetSubmissionEventLog } from '@components/submission-event-log/dataset-submission-event-log'
import { submissionContext, templateContext, usePageTitleContext } from '@contexts'
import { PageHeaderContext } from '@contexts/pageHeader'
import { colors } from '@global/constants'
import { useTemplate } from '@global/hooks/useTemplate'
import * as SnackbarActions from '@global/snackbar/snackbar.actions'
import { lazyImport } from '@global/utils/lazyImport'
import { setTemplate } from '@submissions/submissions.actions'
import { api } from 'api'
import { ROUTES, routeByParam } from 'routes'
import { DatasetData } from './DatasetData'
import { DatasetHistory } from './datasetHistory'
import { DatasetHistoryV2 } from './datasetHistoryV2'
import { DatasetQueryContextProvider } from '../dataset-query-context'
import { useSettings } from './hooks/useSettings'
import { useDatasetQueryContext } from '../dataset-query-context/hooks'
import * as DatasetsActions from '../datasets.actions'
import { useProcessingEffects } from './hooks/useProcessingEffects'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

const { SelectiveDataset } = lazyImport(
  () => import(/* webpackChunkName: "selectiveDataset" */ 'datasets/create-dataset/selective'),
)

const routeableTabs = ['data', 'history']

/**
 * Wrap the dataset details with context providers.
 */
export const DatasetDetails = () => {
  const match = useRouteMatch()
  const { id } = match.params

  return (
    <DatasetQueryContextProvider datasetId={id}>
      <DatasetDetailsContainer />
    </DatasetQueryContextProvider>
  )
}

/**
 * Show dataset tabs; history, data, tracker, selective dataset.
 */
const DatasetDetailsContainer = () => {
  const location = useLocation()
  const match = useRouteMatch()
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = match.params
  const { requireApproval } = useSettings()
  const { setById, fetchTemplate } = useContext(templateContext)
  const { byId, fetchSubmissionByDatasetId } = useContext(submissionContext)
  const { dispatch: pageHeaderDispatch } = useContext(PageHeaderContext)
  const { refetchDataset } = useDatasetQueryContext()
  const { addPrefix, removePrefix } = usePageTitleContext()
  const meta = useSelector(state => state.Datasets.datasetMetadataById[id])
  const [NEW_UI_ENABLED = false, DATASETS_PAGE_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.NEW_SUBMISSION_HISTORY_UI, FeatureDict.DATASETS_PAGE]),
  )

  const submission = useMemo(() => byId[id], [id, byId])
  const { isTrackerVisible } = useProcessingEffects({
    submissionStatus: submission?.status,
    submissionPhase: submission?.phase,
    updateDataCacheStores: () => {
      /**
       * When the tracker is no longer visible (e.g. processing is finished), we need to
       * refetch the dataset, because we may have cached the dataset with no data, and we
       * need to update it. Also, we need to refetch the template, because if this is the
       * first submission the template doesn't have the rollup_dataset_id yet, and it may
       * cause the issue where the dataset has no data even though the submission is processed.
       */
      refetchDataset()
      dispatch(setTemplate({ id: meta?.template_id }))
    },
  })

  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [metaLoading, setMetaLoading] = useState(true)
  const [templateDeleted, setTemplateDeleted] = useState(false)

  const { template } = useTemplate({ templateId: meta?.template_id })
  const isTemplateFetched = !isEmpty(template)

  // Fetch the template or set the existing template to the context.
  useEffect(() => {
    if (!meta?.template_id) return

    // If template is already fetched, set it to the context and return
    // Uses the template already fetched from the useTemplate hook into the context provider.
    if (isTemplateFetched) {
      setLoadingTemplates(false)
      setById(prev => ({ ...prev, [template.id]: template }))
      return
    }

    // If the route is selective edit, don't fetch the template (already available).
    if (matchPath(location.pathname, ROUTES.DATASET_SELECTIVE_EDIT)) {
      setLoadingTemplates(false)
      return
    }

    async function fetchTemplatesOnMount() {
      await fetchTemplate(meta?.template_id)
      setLoadingTemplates(false)
    }

    fetchTemplatesOnMount()
  }, [meta?.template_id, location.pathname])

  // Dataset in pending view do not have submissions against them if pending rollup is enabled.
  // Therefore, this API endpoint would return a 404 error.
  useEffect(() => {
    fetchSubmissionByDatasetId(id)
  }, [id])

  useEffect(() => {
    getMetaData()

    async function getMetaData() {
      setTemplateDeleted(false)

      try {
        const { data } = await api.get(`/api/v1/account/dataset/${id}/metadata`)
        if (data.closed) {
          dispatch(
            SnackbarActions.showSnackbar({
              message: t`All rows in this submission are processed as part of others.`,
              action: (
                <Button
                  onClick={() => {
                    history.push('/codes/submissions')
                  }}
                >
                  <Trans>VIEW ALL SUBMISSIONS</Trans>
                </Button>
              ),
              type: 'INFO',
            }),
          )
        }
        dispatch(DatasetsActions.setDatasetMetadata(data))
      } catch (error) {
        setTemplateDeleted(true)
        setMetaLoading(false)
        console.log('Error fetching metadata', { error })
      } finally {
        setMetaLoading(false)
      }
    }
  }, [id])

  useEffect(() => {
    if (!meta) return
    addPrefix(meta?.identifier || t`Untitled`)

    return () => {
      removePrefix()
    }
  }, [meta])

  // Reset snackbar when submission is completed to avoid showing the processing snackbar even after showing the success message.
  useEffect(() => {
    if (submission?.status === 'Completed') {
      dispatch(SnackbarActions.resetSnackbar())
    }
  }, [submission?.status])

  // Set page header configuration
  useEffect(() => {
    const folderName = template?.folder?.name

    if (!folderName && !template?.name) return

    pageHeaderDispatch({
      type: 'setBreadcrumbs',
      payload: [
        ...(DATASETS_PAGE_ENABLED
          ? [{ label: t`Datasets`, path: '/' }]
          : [{ label: t`Templates`, path: '/codes/templates' }]),
        folderName ? { label: folderName } : null,
        {
          label: template?.name,
          path: routeByParam({ url: ROUTES.TEMPLATE_DATA, params: { id: meta?.template_id } }),
        },
      ].filter(p => p),
    })

    return () => {
      pageHeaderDispatch({
        type: 'setBreadcrumbs',
        payload: [],
      })
    }
  }, [template?.folder?.name, template?.name, meta?.template_id])

  const tab = location.pathname.split('/').reverse()[0]
  const isSelectiveRoute = tab === 'selective'

  if (tab !== 'processing' && !routeableTabs.includes(tab) && !isSelectiveRoute) {
    return <Redirect to={`${tab}/data`} />
  }

  if (templateDeleted) {
    return <TemplateError />
  }

  // if (loadingTemplates || !submission) {
  if (loadingTemplates) {
    return (
      <div style={{ margin: 60, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    )
  }

  return (
    <motion.div
      className="DatasetDetails"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'scroll' }}
    >
      <div style={{ flex: 1 }}>
        <AnimatePresence exitBeforeEnter initial={false}>
          <Suspense fallback={<Loader />}>
            <Switch key={location.path} location={location}>
              <Route
                path={[ROUTES.DATASET_DATA, ROUTES.DATASET_DATA_PROCESSING]}
                render={() => {
                  if (isTrackerVisible && submission?.id) {
                    return (
                      <Tracker
                        submission={submission}
                        datasetId={id}
                        requireApproval={requireApproval}
                        // we need to check in template?.creativeConfiguration?.length to see if the template
                        // has any creative classifications enabled
                        creativeClassificationEnabled={
                          Boolean(template?.creativeConfiguration?.length) ||
                          template?.template_fields?.some(
                            templateField => templateField?.field?.governance_type === 'creative',
                          )
                        }
                      />
                    )
                  }

                  // If processing route is hit and tracker is no longer visible, redirect route path back to data.
                  if (tab === 'processing') {
                    return (
                      <Redirect to={routeByParam({ url: ROUTES.DATASET_DATA, params: { id } })} />
                    )
                  }

                  return (
                    <DatasetData templateId={meta?.template_id} id={id} metaLoading={metaLoading} />
                  )
                }}
              />
              <Route
                exact
                path={ROUTES.DATASET_SELECTIVE_EDIT}
                render={() => <SelectiveDataset templateId={meta?.template_id} />}
              />
              <Route
                path={ROUTES.DATASET_HISTORY}
                render={props =>
                  NEW_UI_ENABLED ? (
                    <DatasetHistoryV2 {...props} requireApproval={requireApproval} />
                  ) : (
                    <DatasetHistory {...props} requireApproval={requireApproval} />
                  )
                }
              />
            </Switch>
          </Suspense>
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

function Tracker({ submission, datasetId, requireApproval, creativeClassificationEnabled }) {
  return (
    <>
      <Paper style={{ margin: 36, backgroundColor: 'white', padding: 12 }}>
        <Typography variant="h3" style={{ marginBottom: 2 }}>
          {t`Processing submission`}
        </Typography>
        <div style={{ marginBottom: 18 }}>
          <Typography variant="caption">
            {t`Your data will automatically be available here as soon as it is finished processing`}
          </Typography>
        </div>
        <DatasetTracker
          submission={submission}
          requireApproval={requireApproval}
          creativeClassificationEnabled={creativeClassificationEnabled}
        />
        <div style={{ marginTop: 18, paddingLeft: 12, paddingRight: 12 }}>
          <LinearProgress />
        </div>
      </Paper>

      <div style={{ margin: 36 }}>
        <DatasetSubmissionEventLog datasetSubmissionId={submission?.id} datasetId={datasetId} />
      </div>
    </>
  )
}

function TemplateError() {
  return (
    <div style={{ textAlign: 'center', padding: 24, paddingTop: 64 }}>
      <div>
        <InfoIcon style={{ fontSize: 48, color: colors.blue.main }} />
      </div>
      <Typography variant="h3" style={{ paddingBottom: 24 }}>
        {t`Unable to load data`}
      </Typography>
      <div>
        {t`This submission belongs to a template that has been deleted. Please try another template.`}
      </div>
    </div>
  )
}
